import PropTypes from 'prop-types';

// formatjs v3 and above do not have proptypes and instead rely on ts.
// this type can be removed when it is no longer used.
export const IntlShape = PropTypes.shape({
  formatMessage: PropTypes.func,
})

export const TRANSCRIPT = 'transcript';
export const RECORDING = 'recording';

export const interactionMediaTypeType = PropTypes.oneOf([
  TRANSCRIPT,
  RECORDING,
]);

export const historyType = PropTypes.shape({
  push: PropTypes.func.isRequired,
});

export const matchType = PropTypes.shape({
  params: PropTypes.shape({}),
});

export const locationType = PropTypes.shape({
  search: PropTypes.string.isRequired,
});

export const stringOrNumberType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number,
]);

export const idType = stringOrNumberType;

export const employeeType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  first_name: PropTypes.string.isRequired,
  last_name: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  custom_id: PropTypes.string,
  group_id: idType.isRequired,
});

export const groupType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  owner: employeeType,
});

// The option id can be an empty string in the case
// when the question supports an "N/A" option, which
// is discounted from the score
export const optionType = PropTypes.shape({
  id: idType,
  text: PropTypes.string.isRequired,
  score: PropTypes.number,
});

export const questionType = PropTypes.shape({
  id: idType.isRequired,
  text: PropTypes.string.isRequired,
  instructions: PropTypes.string,
  possible_score: PropTypes.number,
  options: PropTypes.arrayOf(optionType),
});

export const answerType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  comment: PropTypes.string,
  qa_review_id: PropTypes.number.isRequired,
  qa_question_id: PropTypes.number,
  qa_question: questionType,
  qa_option_id: PropTypes.number,
  qa_option: optionType,
});

export const reviewType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  qa_form_id: PropTypes.number.isRequired,
  employee_id: PropTypes.number.isRequired,
  interaction_id: PropTypes.string.isRequired,
  solicitation_id: PropTypes.number,
  started_at: PropTypes.string.isRequired,
  completed_at: PropTypes.string,
  transcript: PropTypes.string,
  score: PropTypes.number,
  possible_score: PropTypes.number,
  employee: employeeType.isRequired,
  reviewer: employeeType.isRequired,
  count_toward_score: PropTypes.bool,
  filename: PropTypes.string,
});

export const formType = PropTypes.shape({
  id: idType.isRequired,
  name: PropTypes.string.isRequired,
  questions: PropTypes.arrayOf(questionType),
});

export const annotationType = PropTypes.shape({
  id: PropTypes.number,
  start: PropTypes.number,
  stop: PropTypes.number,
  content: PropTypes.string.isRequired,
  comments: PropTypes.string,
  media_type: PropTypes.string,
});

// could this be consolidated with solicitationType?
export const reviewRecSolicitationType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  stars: PropTypes.stars,
  channel: PropTypes.string.isRequired,
  responded_at: PropTypes.string,
  created_at: PropTypes.string.isRequired,
  tags: PropTypes.arrayOf(PropTypes.string),
  employee: employeeType,
  ext_interaction_id: PropTypes.string,
  external_url: PropTypes.string,
});

export const calibrationType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  meeting_at: PropTypes.string,
  notes: PropTypes.string,
  completed_at: PropTypes.string,
  qa_form: formType.isRequired,
  qa_reviews: PropTypes.arrayOf(reviewType).isRequired,
});

export const interactionEventType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  text: PropTypes.string,
  audio_file: PropTypes.string,
  order: PropTypes.number.isRequired,
  occurred_at: PropTypes.string,
  metadata: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    customer: PropTypes.bool,
    public: PropTypes.bool,
    team_member: PropTypes.bool,
  }),
});

const idProp = {
  // new-question-{temporaryHashId()}
  id: idType.isRequired,
};

export const optionProps = PropTypes.shape({
  ...idProp,
  active: PropTypes.bool,
  position: PropTypes.number,
  integer_value: PropTypes.number,
  metadata: PropTypes.shape({}),
  translations: PropTypes.shape({}),
});

export const questionProps = PropTypes.shape({
  page: PropTypes.number.isRequired,
  position: PropTypes.number.isRequired,
  condition: PropTypes.string,

  ...idProp,
  type: PropTypes.string.isRequired,
  show_comment: PropTypes.bool.isRequired,
  show_on_stream: PropTypes.bool.isRequired,
  metadata: PropTypes.shape({}).isRequired,
  translations: PropTypes.shape({}).isRequired,
  options: PropTypes.arrayOf(optionProps),
});

export const pageProps = PropTypes.shape({
  questions: PropTypes.arrayOf(questionProps),
});

const streamOptionType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  translations: PropTypes.shape({}).isRequired,
});

export const streamQuestionType = PropTypes.shape({
  id: PropTypes.number,
  text: PropTypes.string,
  type: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(streamOptionType),
});

export const streamAnswerType = PropTypes.shape({
  question_id: PropTypes.number,
  comment: PropTypes.string,
  selected_option_ids: PropTypes.arrayOf(PropTypes.number),
  questionType: PropTypes.string,
  questionText: PropTypes.string,
});

const customerType = PropTypes.shape({
  name: PropTypes.string,
  email: PropTypes.string,
});

export const solicitationType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  created_at: PropTypes.string.isRequired,
  responded_at: PropTypes.string.isRequired,
  employee: employeeType.isRequired,

  // Flex
  answers: PropTypes.arrayOf(streamAnswerType),

  // Legacy
  stars: PropTypes.number,
  comments: PropTypes.string,
  areas_of_improvement: PropTypes.arrayOf(PropTypes.string),
  areas_of_excellence: PropTypes.arrayOf(PropTypes.string),
  rewards: PropTypes.arrayOf(PropTypes.string),
  answer_text_string: PropTypes.string,
  question_type: PropTypes.string,
  question_text: PropTypes.string,
  answer_comment: PropTypes.string,

  // Service-recovery
  service_recovery_question: PropTypes.string,
  recovered: PropTypes.bool,

  // SolicitationDetails
  channel: PropTypes.string,
  customer: customerType,
  ext_interaction_id: PropTypes.string,
  brand: PropTypes.string,
  custom_properties: PropTypes.shape({}),
  reporting_groups: PropTypes.arrayOf(PropTypes.string),
  tags: PropTypes.arrayOf(PropTypes.string),

  // SolicitationActions
  external_url: PropTypes.string,
  qa_review_ids: PropTypes.arrayOf(PropTypes.number),
  editMode: PropTypes.bool,
  loading: PropTypes.bool,

  // QA Reviews
  qa_review_summaries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      uuid: PropTypes.string,
      type: PropTypes.string,
      employee_id: PropTypes.number,
      reviewer_id: PropTypes.number,
      completed_at: PropTypes.string,
    })
  ),
});

export const styleType = PropTypes.shape({});

export const trendArrowType = {
  trendValue: PropTypes.number,
  higherIsBetter: PropTypes.bool,
};

export const coachingSessionAttachmentType = {
  attachmentId: idType.isRequired,
  attachmentListName: PropTypes.oneOf([
    'metricAttachments',
    'solicitationAttachments',
    'qaReviewAttachments',
    'fileAttachments',
  ]).isRequired,
};

export const fileAttachmentType = {
  notes: PropTypes.string,
  coachingSessionFileId: PropTypes.number,
};

export const fileAttachmentItemType = {
  name: PropTypes.string,
  url: PropTypes.string,
};

export const metricConfigurationType = PropTypes.shape({
  id: idType,
  question_identifier: PropTypes.string,
  metric_name: PropTypes.string,
  translations: PropTypes.shape({}),
  company_id: PropTypes.number,
  config: PropTypes.shape({}),
  metric_params: PropTypes.shape({}),
  order: PropTypes.number,
  created_at: PropTypes.string,
  updated_at: PropTypes.string,
});

export const metricDataType = PropTypes.shape({
  title: PropTypes.string,
  rawValue: PropTypes.number,
  displayValue: PropTypes.string,
  rawDiff: PropTypes.number,
  displayDiff: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  employeeIds: PropTypes.arrayOf(idType),
  timeSeries: PropTypes.arrayOf(PropTypes.shape({})), // todo
  dates: PropTypes.arrayOf(PropTypes.string),
  higherIsBetter: PropTypes.bool,
});

export const surveyQuestionType = {
  id: PropTypes.number,
  identifier: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  translations: PropTypes.shape({}).isRequired,
};

export const followUpType = {
  id: PropTypes.number.isRequired,
  createdAt: PropTypes.string.isRequired,
  createdByEmployeeId: PropTypes.number.isRequired,
  createdByEmployeeFullName: PropTypes.string.isRequired,
  employeeId: PropTypes.number.isRequired,
  followUpableType: PropTypes.string.isRequired,
  followUpableId: PropTypes.number.isRequired,
  note: PropTypes.string.isRequired,
};

export const apiSolicitationType = {
  id: PropTypes.number.isRequired,
  stars: PropTypes.number.isRequired,
  followUpIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  comments: PropTypes.string.isRequired,
  externalUrl: PropTypes.string.isRequired,
};

const flexDataType = PropTypes.shape({}); // TODO

export const flexDataChartPropType = {
  flexData: flexDataType.isRequired,
  loading: PropTypes.bool.isRequired,
  metricConfig: metricConfigurationType.isRequired,
};

export const teamType = PropTypes.shape({
  employees_count: PropTypes.number.isRequired,
  id: idType.isRequired,
  owner: employeeType.isRequired,
});

export const comparisonType = PropTypes.shape({
  unit: PropTypes.string,
  value: PropTypes.number,
  change: PropTypes.number,
  changeText: PropTypes.oneOf(['none', 'increase', 'decrease']).isRequired,
  comparisonValue: PropTypes.number,
  displayValue: PropTypes.string,
  displayChangeValue: PropTypes.string,
  displayComparisonValue: PropTypes.string,
});

export const comparisonOptionType = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  shortLabel: PropTypes.string.isRequired,
});

export const metricType = PropTypes.shape({
  question_identifier: PropTypes.string,
  metric_name: PropTypes.string,
  questionIdentifier: PropTypes.string,
  metricName: PropTypes.string,
});

export const qaParamsType = PropTypes.shape({
  team_ids: PropTypes.arrayOf(idType),
  by_employee_id: PropTypes.arrayOf(idType),
  completed_from: PropTypes.string,
  completed_to: PropTypes.string,
  autofailed: PropTypes.bool,
});

// If you're using GQL, use this type
export const metricResultType = PropTypes.shape({
  displayValue: PropTypes.string,
  higherIsBetter: PropTypes.bool,
  max: PropTypes.number,
  metricName: PropTypes.string,
  min: PropTypes.number,
  questionIdentifier: PropTypes.string,
  title: PropTypes.string,
  totalResults: PropTypes.number,
  value: PropTypes.number,
});
